import { Mobile, ProjectHelper } from '@bimser/common';
import { BCDataGrid, findMessage } from '@bimser/components';
import { MLHelper, compareMatchWithCollection, isValidJSON } from '@bimser/core';
import * as FormControls from '@bimser/form-controls';
import IconOk from '@bimser/icons/16/ok';
import GetIcon16ByName from '@bimser/icons/lib/getIcon16ByName';
import get from 'lodash/get';
import React from 'react';
import { ModuleImporter } from '../../../../helpers/sdk/ModuleImporter';
import Styles from '../assets/Styles.scss';
import { ISelectionListCProps, ISelectionListProps } from '../entities';
import { calculateSelectedFilterOperation, setFunctionsForMobile } from '../helpers';

export default class SelectionList extends React.Component<
	ISelectionListCProps & ISelectionListProps
> {
	bcDataGridRef = React.createRef<BCDataGrid.Entities.BCDataGridRef>();
	valueExpr: string;
	defaultSelectedRowKeys: any[];
	columns: FormControls.FormControlEntities.IDataGridColumnCollection;
	store: BCDataGrid.Components.CustomStore;
	toolbarOkButtonOptions = {
		icon: IconOk.info.getElement(),
		text: findMessage.get('100004'),
		hint: findMessage.get('100004'),
		onClick: () => {
			this.btnOkClicked();
		}
	};

	constructor(props: ISelectionListCProps & ISelectionListProps) {
		super(props);

		this.generateColumns = this.generateColumns.bind(this);
		this.btnOkClicked = this.btnOkClicked.bind(this);
		this.renderCellWithIconMatcher = this.renderCellWithIconMatcher.bind(this);
		this.customLoad = this.customLoad.bind(this);
		this.customSave = this.customSave.bind(this);

		this.columns = this.props.childFormData.args.options.columns;
		this.valueExpr = this.columns.find(column => column.isPrimaryKey).name;
		this.defaultSelectedRowKeys = this.props.childFormData.extendedArgs.rows.map(
			row => row.data[this.valueExpr]
		);
		this.store = new BCDataGrid.Components.CustomStore({
			key: this.valueExpr,
			load: async () => {
				const rows = await this.loadRows();

				return {
					data: rows,
					totalCount: rows?.length
				};
			}
		});

		setFunctionsForMobile.call(this);
	}

	async btnOkClicked() {
		let rows = await this.convertRowsToCollection();

		if (
			this.props.childFormData.args.doNotOverrideSelectedRows &&
			this.props.childFormData.extendedArgs.rows.length > 0
		) {
			rows = rows.map(currentRow => {
				const matchingRow = this.props.childFormData.extendedArgs.rows.find(prevRow => {
					const prevRowPrimary = prevRow.cells.find(c => c.name === this.valueExpr);
					const currentRowPrimary = currentRow.cells.find(c => c.name === this.valueExpr);

					return prevRowPrimary?.value === currentRowPrimary?.value;
				});

				return matchingRow || currentRow;
			});
		}

		this.props.onSelectedChange({
			panelId: this.props.panelGuid,
			panelType: this.props.panelType,
			data: {
				rows,
				args: this.props.childFormData,
				updatePanelId: this.props.childFormData.updatePanelId
			}
		});
	}

	async loadRows() {
		try {
			const response = await this.getDataSourceData();

			const { sdkVersion } = ProjectHelper.getTargetAgentProject(
				this.props.childFormData.projectName
			);

			const { FormControlsHelpers } = await ModuleImporter(sdkVersion).SDK().Helpers();

			const rows = response ? FormControlsHelpers.getResultData(response.data) : null;

			Mobile.MobileView.setSelectionFormRows({
				rows,
				selectedRows: this.props.childFormData.extendedArgs.rows.map(row => row.data),
				primaryColumnName: this.valueExpr
			});

			return rows;
		} catch (error) {
			console.error(error);
		}
	}

	getDataSourceData() {
		if (
			!this.props.childFormData.args.dataSource.runAtServer &&
			this.props.childFormData.args.dataSource.name &&
			this.props.childFormData.endPoint
		) {
			const paramArgs = FormControls.FormControlsHelpers.getDataSourceParams(
				this.props.childFormData.args.dataSource,
				null
			);

			if (paramArgs.isValid) {
				return ProjectHelper.RunPromiseFormService({
					serviceName: this.props.childFormData.args.dataSource.name,
					projectName: this.props.childFormData.projectName,
					formName: 'DataSource',
					requestParameters: { ...paramArgs.requestPayload },
					requestConfig: {
						headers: {
							'Content-Type': 'application/json; charset=utf-8'
						},
						cache: true
					}
				});
			}

			throw new Error('Invalid parameters for selection form');
		}

		throw new Error('Invalid configuration for selection form');
	}

	async convertRowsToCollection() {
		const selectedRows = await this.bcDataGridRef.current.instance().getSelectedRowsData();

		return selectedRows.map((item, index) => {
			const cell = {
				cellData: {},
				cells: Object.keys(item)
					.filter(key => this.columns.findIndex(column => column.name === key) > -1)
					.map(columnName => {
						const targetColumn =
							this.props.childFormData.args.targetDataGridColumns?.find(
								i => i.name === columnName
							);

						const sourceColumn =
							this.props.childFormData.args.options.columnMapping.find(
								({ source }) => source === columnName
							);

						const value = item[sourceColumn?.source || columnName];

						return {
							editType: targetColumn?.editType,
							entityPath: (targetColumn?.editControl as any)?.EntityPath,
							name: sourceColumn?.target || columnName,
							text:
								targetColumn?.editType === 'multiLanguageTextBox'
									? MLHelper.getMLText(value)
									: value,
							value
						};
					}),
				data: {},
				index
			};

			cell.data = cell.cells.reduce(
				(result, current) => ({ ...result, [current.name]: current.value }),
				{}
			);

			return cell;
		});
	}

	renderToolbar() {
		return (
			<BCDataGrid.Components.Toolbar>
				<BCDataGrid.Components.Item
					key="ok"
					widget="dxButton"
					locateInMenu="auto"
					location="before"
					options={
						this.props.childFormData.args.okButton
							? {
									icon: GetIcon16ByName(
										this.props.childFormData.args.okButton.icon,
										IconOk
									).info.getElement(),
									text: MLHelper.getMLText(
										this.props.childFormData.args.okButton.text
									),
									hint: MLHelper.getMLText(
										this.props.childFormData.args.okButton.title
									),
									onClick: () => {
										this.btnOkClicked();
									}
							  }
							: this.toolbarOkButtonOptions
					}
				/>
				<BCDataGrid.Components.Item name="searchPanel" />
				<BCDataGrid.Components.Item name="columnChooserButton" />
			</BCDataGrid.Components.Toolbar>
		);
	}

	renderSummary() {
		return (
			<BCDataGrid.Components.Summary>
				{this.columns.flatMap(
					column =>
						column.summaryTypePrefixes?.map(summaryType => (
							<BCDataGrid.Components.TotalItem
								key={`${column.name}_${summaryType.type}`}
								column={column.name}
								summaryType={summaryType.type}
								valueFormat={FormControls.FormControlsHelpers.calculateFormat(
									column as any
								)}
								displayFormat={
									summaryType.suffix ? `${summaryType.suffix}: {0}` : undefined
								}
							/>
						))
				)}
			</BCDataGrid.Components.Summary>
		);
	}

	dbTypeToDataType(column: (typeof this.columns)[0]) {
		switch (column.dbType.type) {
			case 'decimal':
			case 'int':
				return 'number';
			case 'date':
				return 'date';
			case 'dateTime':
			case 'time':
				return 'datetime';
			case 'none':
			case 'string':
			case 'object':
			default:
				return 'string';
		}
	}

	generateColumns() {
		return this.columns.map<BCDataGrid.Entities.IColumnProps>(bcColumn => {
			const dxColumn: BCDataGrid.Entities.IColumnProps = {
				name: bcColumn.name,
				dataField: bcColumn.name,
				caption: MLHelper.getMLText(bcColumn.caption) || bcColumn.name.toLocaleUpperCase(),
				alignment: bcColumn.align,
				width: bcColumn.width,
				dataType: this.dbTypeToDataType(bcColumn),
				visible: bcColumn.visible,
				allowSorting: bcColumn.sorting.enabled,
				selectedFilterOperation: calculateSelectedFilterOperation(
					bcColumn.filtering.operation
				)
			};

			if (bcColumn.iconSourceField && bcColumn.iconMatchers?.length) {
				dxColumn.cellRender = this.renderCellWithIconMatcher;
			} else if (bcColumn.editType === 'multiLanguageTextBox') {
				dxColumn.cellRender = this.renderCellMultiLanguageTextBox;
			} else if (bcColumn.editType === 'comboBox') {
				dxColumn.cellRender = this.renderCellComboBox;
			}

			switch (bcColumn.editType) {
				case 'numberBox': {
					dxColumn.format = FormControls.FormControlsHelpers.calculateFormat(
						bcColumn as any
					);
					break;
				}
				default: {
					break;
				}
			}

			return dxColumn;
		});
	}

	renderCellWithIconMatcher(args) {
		const bcColumn = this.columns.find(column => column.name === args.column.name);

		const iconSourceFieldsValue = get(args.row.data, bcColumn.iconSourceField);

		const match = bcColumn.iconMatchers.find(matcher =>
			compareMatchWithCollection({
				value: iconSourceFieldsValue,
				compareValue: matcher.value,
				dataType: bcColumn.dbType.type,
				editType: bcColumn.editType
			})
		);

		if (match) {
			const Icon = GetIcon16ByName(match.icon);

			switch (bcColumn.editType) {
				case 'multiLanguageTextBox':
					return (
						<div className={Styles.cellWithIcon}>
							<Icon className={Styles.icon} />
							{MLHelper.getMLText(args.value)}
						</div>
					);

				case 'comboBox':
					return (
						<div className={Styles.cellWithIcon}>
							<Icon className={Styles.icon} />
							{args.value?.text || args.value?.value || args.text || args.value}
						</div>
					);
				default:
					return (
						<div className={Styles.cellWithIcon}>
							<Icon className={Styles.icon} />
							{args.text}
						</div>
					);
			}
		}
	}

	renderCellMultiLanguageTextBox(args) {
		return MLHelper.getMLText(args.value);
	}

	renderCellComboBox(args) {
		const displayValue = args.value?.text || args.value?.value || args.text || args.value;

		if (typeof displayValue === 'object' && displayValue) {
			return MLHelper.getMLText(displayValue);
		}

		return displayValue;
	}

	customSave({ searchText, columns, pageIndex, pageSize }) {
		localStorage.setItem(
			this.props.childFormData.args.gridId,
			JSON.stringify({ searchText, columns, pageIndex, pageSize })
		);
	}

	customLoad() {
		const state = localStorage.getItem(this.props.childFormData.args.gridId);

		if (state) {
			return isValidJSON(state) || {};
		}

		return {};
	}

	render() {
		const options = this.props.childFormData.args.options;

		return (
			<div className={Styles.openSelectionList}>
				<BCDataGrid.Components.DataGrid
					name={BCDataGrid.Entities.DataGrids.SelectionForm}
					ref={this.bcDataGridRef}
					dataSource={this.store}
					remoteOperations={false}
					allowColumnReordering={options.columnSettings.orderable}
					allowColumnResizing={options.columnSettings.resizeable}
					columnResizingMode={options.columnSettings.resizingMode}
					defaultSelectedRowKeys={this.defaultSelectedRowKeys}
					width="100%"
					height="100%"
				>
					<BCDataGrid.Components.StateStoring
						enabled={this.props.childFormData.args.cacheSettingsEnabled}
						customSave={this.customSave}
						customLoad={this.customLoad}
						savingTimeout={500}
						type="custom"
					/>

					{options.selection.enabled && (
						<BCDataGrid.Components.Selection
							allowSelectAll={options.selection.showSelectAll}
							mode={options.selection.mode}
							showCheckBoxesMode={options.selection.showCheckBoxesMode}
							selectAllMode={options.selection.selectAllMode}
						/>
					)}

					<BCDataGrid.Components.Paging
						enabled={options.paging.enabled}
						defaultPageSize={options.paging.pageSize}
						defaultPageIndex={options.paging.currentPage - 1}
					/>

					<BCDataGrid.Components.Pager
						visible={options.paging.enabled}
						allowedPageSizes={options.paging.pagingSizes}
						showPageSizeSelector
					/>

					<BCDataGrid.Components.SearchPanel visible={options.searching.enabled} />

					<BCDataGrid.Components.ColumnChooser
						enabled={options.columnSettings.showColumnChooser}
					/>

					<BCDataGrid.Components.FilterRow
						visible={options.filtering.enabled}
						showOperationChooser={options.filtering.enabled}
					/>

					<BCDataGrid.Components.Sorting mode={options.sorting.mode} />

					{this.renderSummary()}
					{this.renderToolbar()}
					{this.generateColumns().map(column => (
						<BCDataGrid.Components.Column
							key={column.name}
							{...column}
						/>
					))}
				</BCDataGrid.Components.DataGrid>
			</div>
		);
	}
}
